import { goalAtRisk, type ForecastOverview, type Me, type Overview, type ProjectOverview } from '@ft/shared';
import { Fragment, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ExportButton } from '../components/ExportButton';
import { SavedViews } from '../components/SavedViews';
import { Cust, Loading, useLoad } from '../components/ui';
import { useI18n, type T } from '../i18n';
import { useRememberedFilter } from '../lib/remember';
import { canSeePath, getAreaName } from '../lib/area';
import { eur, keur, pct, pname, skeur } from '../lib/format';
import { Avatar } from '../lib/people';

const TONE = { noTarget: 'neu', uncovered: 'neg', behind: 'crit', onTrack: 'pos' } as const;

/** Fortschrittsbalken: Rahmen = bestellt, Füllung = abgerechnet, Strich = Soll heute. Alles relativ zum größten Wert. */
export function Track({ p, pace, t, expected }: { p: ProjectOverview; pace: number; t: T; expected?: number | null }) {
  const scale = Math.max(p.targetCents ?? 0, p.orderedCents, p.billedCents, expected ?? 0) || 1;
  const w = (v: number) => `${Math.min(100, (v / scale) * 100)}%`;
  return (
    <div className="track" role="img" aria-label={t('pz.trackLabel', { project: p.name, billed: eur(p.billedCents), ordered: eur(p.orderedCents), target: p.targetCents !== null ? eur(p.targetCents) : '–' })}>
      <div className="ord" style={{ width: w(p.orderedCents) }} />
      <div className="inv" style={{ width: w(p.billedCents) }} />
      {p.targetCents !== null && <div className="tgt" style={{ left: w(p.targetCents) }} />}
      {p.targetCents !== null && <div className="pace" style={{ left: w(p.targetCents * pace) }} />}
      {expected != null && p.targetCents !== null && <div className={`exp${goalAtRisk(p.targetCents, expected) ? ' risk' : ''}`} style={{ left: w(expected) }} title={t('pz.legend.expected')} />}
    </div>
  );
}

export function HealthPill({ h, t }: { h: ProjectOverview['health']; t: T }) {
  return <span className={`pill ${TONE[h]}`}>{t(`pz.health.${h}`)}</span>;
}

export function ProjectsPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  useRememberedFilter('projects');
  const thisYear = new Date().getFullYear();
  const year = Number(sp.get('jahr') ?? thisYear);
  const { data, error } = useLoad<Overview>(`/api/overview?year=${year}`);
  const { data: fc } = useLoad<ForecastOverview>(canSeePath(me, '/forecast') ? `/api/forecast?year=${year}` : null);
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const fcust = sp.get('kunde') ?? '';
  const fpm = sp.get('pm') ?? '';
  const fh = sp.get('status') ?? '';
  const [closed, setClosed] = useState<Set<string>>(new Set());

  const expOf = useMemo(() => new Map((fc?.projects ?? []).map((x) => [x.id, x.expectedTotalCents])), [fc]);
  const exp = (p: ProjectOverview) => (fc ? expOf.get(p.id) ?? p.billedCents : null);
  const risk = (p: ProjectOverview) => goalAtRisk(p.targetCents, exp(p));
  const gaps = (p: ProjectOverview) => !p.contract || !p.pm || p.targetCents === null;

  const pms = useMemo(() => [...new Map((data?.projects ?? []).flatMap((p) => [p.pm, p.deputy]).filter((x): x is NonNullable<typeof x> => !!x).map((u) => [u.id, u])).values()].sort((a, b) => a.name.localeCompare(b.name)), [data]);
  const base = (data?.projects ?? []).filter((p) => (!fcust || p.customer.name === fcust) && (!fpm || (fpm === 'none' ? !p.pm : p.pm?.id === fpm || p.deputy?.id === fpm)));
  const match = (p: ProjectOverview, h: string) => !h || (h === 'risk' ? risk(p) : h === 'gaps' ? gaps(p) : p.health === h);
  const list = base.filter((p) => match(p, fh));
  // Kunden nach Ziel absteigend, Projekte nach Abstand zum Ziel (größte Lücke zuerst)
  const delta = (p: ProjectOverview) => (p.targetCents ? (exp(p) ?? p.billedCents) - p.targetCents : Number.MAX_SAFE_INTEGER);
  const groups = useMemo(() => {
    const m = new Map<string, ProjectOverview[]>();
    for (const p of list) m.set(p.customer.name, [...(m.get(p.customer.name) ?? []), p]);
    return [...m].map(([name, ps]) => ({ name, ps: ps.sort((a, b) => Number(a.empty) - Number(b.empty) || delta(a) - delta(b)) }))
      .sort((a, b) => b.ps.reduce((s, p) => s + (p.targetCents ?? 0), 0) - a.ps.reduce((s, p) => s + (p.targetCents ?? 0), 0));
  }, [list, expOf]);
  const sumOf = (ps: ProjectOverview[]) => ({
    target: ps.reduce((s, p) => s + (p.targetCents ?? 0), 0), ordered: ps.reduce((s, p) => s + p.orderedCents, 0), billed: ps.reduce((s, p) => s + p.billedCents, 0),
    exp: ps.reduce((s, p) => s + (exp(p) ?? 0), 0),
  });
  const all = sumOf(list);
  const pace = data?.pace ?? 0;
  const open = (id: string) => nav(`/projekte/${id}`);

  const riskList = base.filter(risk).sort((a, b) => delta(a) - delta(b));
  const behind = base.filter((p) => p.health === 'behind').length;
  const totalAll = sumOf(base);
  const lead = data && totalAll.target ? [
    t('pz.say.base', { n: base.length, target: keur(totalAll.target), billed: `<b>${keur(totalAll.billed)}</b>`, p: pct(totalAll.billed / totalAll.target), pace: pct(pace) }),
    behind ? t('pz.say.behind', { n: behind }) : '',
    fc ? (riskList.length ? t('pz.say.risk', { n: `<b>${riskList.length}</b>`, top: riskList.slice(0, 3).map((p) => `${pname(p)} (${skeur(delta(p))})`).join(', ') }) : t('pz.say.noRisk')) : '',
  ].filter(Boolean).join(' ') : '';

  const segs: [string, string, number][] = [
    ['', t('common.all'), base.length],
    ...(fc ? [['risk', t('pz.risk'), riskList.length] as [string, string, number]] : []),
    ['behind', t('pz.health.behind'), behind],
    ['gaps', t('pz.gaps'), base.filter(gaps).length],
  ];
  const filtered = !!(fcust || fpm || fh);

  return (
    <section className="page">
      <div className="pagehead">
        <div><div className="crumb">{t('crumb.root', { area: getAreaName() })}</div><h1>{t('pz.title')}</h1></div>
        <span className="spacer" />
        <SavedViews />
        <ExportButton year={year} />
        <div className="fld">
          <label htmlFor="pz-y">{t('pz.year')}</label>
          <select id="pz-y" className="yearsel" value={year} onChange={(e) => set('jahr', e.target.value === String(thisYear) ? '' : e.target.value)}>
            {[thisYear - 1, thisYear, thisYear + 1].map((y) => <option key={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {!data ? <Loading error={error} /> : (
        <>
          {lead && <p className="say" dangerouslySetInnerHTML={{ __html: lead }} />}

          <div className="chips" role="group" aria-label={t('common.filter')}>
            <div className="seg" role="group" aria-label={t('common.status')}>
              {segs.map(([k, label, n]) => <button key={k} aria-pressed={fh === k} onClick={() => set('status', k)}>{label} <em className="cnt">{n}</em></button>)}
            </div>
            <span className="spacer" />
            <select aria-label={t('common.pm')} className="chipsel" value={fpm} onChange={(e) => set('pm', e.target.value)}>
              <option value="">{t('pz.allPms')}</option>
              {pms.map((u) => <option key={u.id} value={u.id}>{u.name}{u.id === me.id ? ` ${t('pz.me')}` : ''}</option>)}
              <option value="none">{t('common.withoutPm')}</option>
            </select>
            {filtered && <button className="btn ghost" onClick={() => setSp(year === thisYear ? new URLSearchParams() : new URLSearchParams({ jahr: String(year) }), { replace: true })}>{t('pz.removeFilter')}</button>}
          </div>

          <div className="box">
            <div className="panel-h">
              <h2>{fcust ? `${fcust} · ` : ''}{t('pz.count', { n: list.length })}</h2>
              <span className="spacer" />
              <div className="legend">
                <span><i className="lg" style={{ background: 'var(--neu-bar)' }} />{t('pz.legend.billed')}</span>
                <span><i className="lg" style={{ border: '1.5px solid var(--neu-bar)' }} />{t('pz.legend.ordered')}</span>
                <span><i className="lg pacekey" />{t('pz.legend.pace', { p: pct(pace) })}</span>
                {fc && <span><i className="lg expkey" />{t('pz.legend.expected')}</span>}
              </div>
            </div>
            <div className="tablewrap">
              {!list.length ? <div className="empty">{t('pz.empty')}</div> : (
                <table className="pztable">
                  <thead><tr>
                    <th>{t('common.project')}</th><th>{t('common.pm')}</th>
                    <th className="r">{t('pz.col.target')}</th><th className="r">{t('pz.col.ordered')}</th><th className="r">{t('pz.col.billed')}</th>
                    {fc && <th className="r">{t('pz.col.expected')}</th>}
                    <th style={{ minWidth: 160 }}>{t('pz.col.progress')}</th><th>{t('common.status')}</th>
                  </tr></thead>
                  <tbody>
                    {groups.map((g) => {
                      const sm = sumOf(g.ps);
                      const shut = closed.has(g.name);
                      return (
                        <Fragment key={g.name}>
                          <tr className="grouprow click" onClick={() => setClosed((c) => { const n = new Set(c); if (n.has(g.name)) n.delete(g.name); else n.add(g.name); return n; })}>
                            <td colSpan={2}><span className="caret" aria-hidden="true">{shut ? '▸' : '▾'}</span> <Cust name={g.name} /> <span className="muted small">{t('pz.count', { n: g.ps.length })}</span></td>
                            <td className="r num"><b>{sm.target ? eur(sm.target) : '–'}</b></td>
                            <td className="r num">{eur(sm.ordered)}</td>
                            <td className="r num">{eur(sm.billed)}</td>
                            {fc && <td className="r num"><b>{eur(sm.exp)}</b>{sm.target > 0 && <span className="small" style={{ marginLeft: 6, color: sm.exp < sm.target ? 'var(--neg)' : 'var(--pos)' }}>{skeur(sm.exp - sm.target)}</span>}</td>}
                            <td /><td />
                          </tr>
                          {!shut && g.ps.map((p) => {
                            const e = exp(p);
                            const d = p.targetCents && e !== null ? e - p.targetCents : null;
                            return (
                              <tr key={p.id} className={`click${p.empty ? ' emptyrow' : ''}`} tabIndex={0} onClick={() => open(p.id)} onKeyDown={(ev) => { if (ev.key === 'Enter') open(p.id); }}>
                                <td>
                                  <Link className="plink" to={`/projekte/${p.id}`} onClick={(ev) => ev.stopPropagation()} title={p.shortName ? p.name : undefined}>{pname(p)}</Link>
                                  {!p.contract && <span className="pill crit mini">{t('pz.noContract')}</span>}
                                  {!p.pm && <span className="pill crit mini">{t('common.withoutPm')}</span>}
                                  {p.targetCents === null && <span className="pill neu mini">{t('pz.health.noTarget')}</span>}
                                  {p.contract && !p.contract.filed && <span className="pill crit mini" title={t('ct.notFiledHint')}>{t('ct.notFiled')}</span>}
                                  <div className="muted small">{p.contract ? <Link className="sublink" to={`/vertraege/${p.contract.id}`} onClick={(ev) => ev.stopPropagation()}>{p.contract.name}</Link> : p.empty ? <i>{t('pz.emptyHint')}</i> : '–'}</div>
                                </td>
                                <td>{p.pm ? <Avatar id={p.pm.id} name={p.pm.name} initials={p.pm.initials} /> : p.deputy ? <span className="muted small" title={p.deputy.name}>{t('pz.deputyShort', { i: p.deputy.initials })}</span> : <span className="muted">–</span>}</td>
                                <td className="r num">{p.targetCents !== null ? eur(p.targetCents) : '–'}</td>
                                <td className="r num">{eur(p.orderedCents)}{p.targetCents ? <div className="muted small">{pct(p.orderedCents / p.targetCents)}</div> : null}</td>
                                <td className="r num">{eur(p.billedCents)}{p.targetCents ? <div className="muted small">{pct(p.billedCents / p.targetCents)}</div> : null}</td>
                                {fc && <td className="r num">{e !== null ? eur(e) : '–'}{d !== null && <div className="small" style={{ color: risk(p) ? 'var(--neg)' : 'var(--pos)' }}>{t('pz.toTarget', { d: skeur(d) })}</div>}</td>}
                                <td><Track p={p} pace={pace} t={t} expected={e} /></td>
                                <td>{risk(p) ? <><span className="pill neg">{t('pz.risk')}</span>{p.health === 'behind' && <div className="muted small">{t('pz.andBehind')}</div>}</> : <HealthPill h={p.health} t={t} />}</td>
                              </tr>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan={2}>{t('common.sum')}</td>
                    <td className="r num">{eur(all.target)}</td><td className="r num">{eur(all.ordered)}</td><td className="r num">{eur(all.billed)}</td>
                    {fc && <td className="r num">{eur(all.exp)}{all.target > 0 && <span className="small" style={{ marginLeft: 6, color: all.exp < all.target ? 'var(--neg)' : 'var(--pos)' }}>{skeur(all.exp - all.target)}</span>}</td>}
                    <td /><td />
                  </tr></tfoot>
                </table>
              )}
            </div>
          </div>
          <p className="muted small" style={{ margin: 0 }}>{t('pz.healthHelp')} {fc ? t('pz.riskHelp') : ''}</p>
        </>
      )}
    </section>
  );
}
